import React, { useState, useEffect, useCallback, useMemo } from "react"
import {
    Box, Paper, List, ListItem, ListItemText, ListItemIcon, Divider, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Stack, Alert, LinearProgress, IconButton, Chip, FormControlLabel, Switch, Checkbox
} from "@mui/material"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SettingsIcon from '@mui/icons-material/Settings'
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import { MODALITY_OPTIONS } from "../../../../constants/Consult"
import ModalityGrid from "./ModalityGrid"
import RecurrenceEditor from "./RecurrenceEditor"
import { ModalityWorklistFields } from "../shared/ModalityWorklist"
import DetailPanelContent from "../shared/DetailPanelContent"
import PdfPreview from "../shared/PdfPreview"
import ReportingData from "../Studies/Teleconsulation/Respond/ReportingData"
import {
    GetModalitySchedule, BookModalitySlot, UnbookModalitySlot, UpdateModalitySlot,
    CreateModalityLimitTemplate, UpdateModalityLimitTemplate,
    UpsertModalityLimitOverride,
    GetUnscheduledStudies, GetStudy, GetToken, MwlEdit, DuplicateStudy,
    Subscribe, Unsubscribe, UpdateOrgSettings
} from "../../../../api"
import { useNavigate } from "react-router-dom"

const utcDow = (dateStr) => { const [y, m, d] = dateStr.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay() }
const utcDayOfMonth = (dateStr) => Number(dateStr.split('-')[2])
const getWeekOfMonth = (dateStr) => { const [y, m, day] = dateStr.split('-').map(Number); const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay(); let count = 0; for (let i = 1; i <= day; i++) { if (new Date(Date.UTC(y, m - 1, i)).getUTCDay() === dow) count++ } return count }
const todayStr = () => new Date().toISOString().split('T')[0]

const ModalityScheduleCalendar = ({ access, socketStatus }) => {
    const navigate = useNavigate()
    const orgId = access.org.id
    const canEdit = !!access.admin
    const mwlEnabled = !!access.org.settings?.modalityWorklist
    const aetitle = Array.isArray(access.org.aetitle) ? access.org.aetitle[0] : access.org.aetitle

    const [date, setDate] = useState(todayStr())
    const [slots, setSlots] = useState([])
    const [limits, setLimits] = useState({})
    const [countByModality, setCountByModality] = useState({})
    const [unscheduled, setUnscheduled] = useState([])
    const [loading, setLoading] = useState(false)
    const [collapsed, setCollapsed] = useState(false)

    // Dialogs
    const [slotDetailDialog, setSlotDetailDialog] = useState(null)
    const [studyDetailDialog, setStudyDetailDialog] = useState(null)
    const [mwlPromptDialog, setMwlPromptDialog] = useState(null)
    const [mwlData, setMwlData] = useState({})
    const [mismatchDialog, setMismatchDialog] = useState(null)
    const [duplicateDialog, setDuplicateDialog] = useState(null)
    const [limitDialog, setLimitDialog] = useState(null)
    const [limitRecurring, setLimitRecurring] = useState(false)
    const [limitRecForm, setLimitRecForm] = useState({ pattern: 'weekly', interval: 1, daysOfWeek: [], monthlyMode: 'weekOfMonth', dayOfMonth: 1, weekOfMonth: 1 })

    const [modalityColumns, setModalityColumns] = useState(
        () => access.org.settings?.modalityColumns?.length ? access.org.settings.modalityColumns : MODALITY_OPTIONS
    )
    const [columnsDialog, setColumnsDialog] = useState(false)
    const [columnsEdit, setColumnsEdit] = useState([])

    const mwlInitialData = useMemo(() => {
        if (!mwlPromptDialog) return {}
        return {
            ScheduledStartDate: date.replace(/-/g, ''),
            ScheduledStartTime: mwlPromptDialog.startTime?.replace(':', '') + '00',
            Modality: mwlPromptDialog.modality
        }
    }, [mwlPromptDialog, date])

    const fetchAll = useCallback(() => {
        setLoading(true)
        return Promise.all([
            GetModalitySchedule(orgId, date),
            GetUnscheduledStudies(orgId, 'imaging')
        ]).then(([sched, unsch]) => {
            setSlots(sched.slots || [])
            setLimits(sched.limits || {})
            setCountByModality(sched.countByModality || {})
            setUnscheduled(unsch || [])
        }).catch(() => {}).finally(() => setLoading(false))
    }, [orgId, date])

    useEffect(() => { fetchAll() }, [fetchAll, socketStatus])

    useEffect(() => {
        Subscribe({ coll: 'schedule', type: orgId }, fetchAll)
        return () => socketStatus && Unsubscribe({ coll: 'schedule', type: orgId })
    }, [orgId, fetchAll, socketStatus])

    // --- Book flow ---
    const executeBook = useCallback(async (studyId, modality, startTime, endTime, study) => {
        await BookModalitySlot(orgId, { studyId, modality, date, startTime, endTime })
        await fetchAll()
        if (mwlEnabled) {
            if (study?.modalityWorklist || study?._mwlUpdated) {
                // Already has MWL or was just updated — update date/time only if not already done
                if (study?.modalityWorklist && !study?._mwlUpdated) {
                    MwlEdit(studyId, {
                        ScheduledStartDate: date.replace(/-/g, ''),
                        ScheduledStartTime: startTime.replace(':', '') + '00',
                        aetitle
                    }).catch(() => {})
                }
            } else {
                setMwlData({})
                setMwlPromptDialog({ studyId, modality, startTime, study })
            }
        }
    }, [orgId, date, fetchAll, mwlEnabled, aetitle])

    const handleDrop = useCallback(async (studyId, modality, startTime, endTime) => {
        const study = unscheduled.find(s => (s.id || s._id) === studyId)
            || slots.find(s => String(s.studyId?.id || s.studyId?._id || s.studyId) === studyId)?.studyId

        // Already scheduled on this modality scheduler — offer duplicate
        const existingSlot = slots.find(s => String(s.studyId?.id || s.studyId?._id || s.studyId) === studyId)
        if (existingSlot && existingSlot.modality !== modality) {
            setDuplicateDialog({ studyId, study, fromModality: existingSlot.modality, toModality: modality, startTime, endTime })
            return
        }

        // MWL modality mismatch
        const existingMwlModality = study?.modalityWorklist?.ScheduledProcedureStepSequence?.[0]?.Modality
        if (existingMwlModality && existingMwlModality !== modality) {
            setMismatchDialog({ studyId, study, existingModality: existingMwlModality, newModality: modality, startTime, endTime })
            return
        }

        await executeBook(studyId, modality, startTime, endTime, study)
    }, [unscheduled, slots, executeBook])

    const handleMismatchConfirm = useCallback(async () => {
        const { studyId, study, newModality, startTime, endTime } = mismatchDialog
        setMismatchDialog(null)
        // MwlEdit with Modality updates ScheduledProcedureStepSequence.0.Modality via the controller
        if (study?.modalityWorklist) {
            await MwlEdit(studyId, {
                Modality: newModality,
                ScheduledStartDate: date.replace(/-/g, ''),
                ScheduledStartTime: startTime.replace(':', '') + '00',
                aetitle
            }).catch(() => {})
        }
        // Book the slot — pass a truthy modalityWorklist so executeBook skips the MWL prompt (already updated above)
        await executeBook(studyId, newModality, startTime, endTime, { ...study, _mwlUpdated: true })
    }, [mismatchDialog, executeBook, aetitle, date])

    const handleDuplicateConfirm = useCallback(async () => {
        const { studyId, toModality, startTime, endTime } = duplicateDialog
        setDuplicateDialog(null)
        try {
            const dup = await DuplicateStudy(studyId)
            const dupId = dup.id || dup._id
            await executeBook(dupId, toModality, startTime, endTime, dup)
        } catch (e) {}
    }, [duplicateDialog, executeBook])

    const handleUnbook = useCallback(async (studyId) => {
        await UnbookModalitySlot(orgId, { studyId })
        setSlotDetailDialog(null)
        fetchAll()
    }, [orgId, fetchAll])

    const handleUpdateSlotTime = useCallback(async (slotId, startTime, endTime) => {
        await UpdateModalitySlot(orgId, { slotId, startTime, endTime })
        setSlotDetailDialog(d => d ? { ...d, slot: { ...d.slot, startTime, endTime } } : d)
        fetchAll()
    }, [orgId, fetchAll])

    // --- Limit dialog ---
    const handleHeaderClick = useCallback((modality, limit, count) => {
        if (!canEdit) return
        const dow = utcDow(date)
        setLimitRecurring(false)
        setLimitRecForm({
            pattern: 'weekly', interval: 1, daysOfWeek: [dow],
            monthlyMode: 'weekOfMonth', dayOfMonth: utcDayOfMonth(date), weekOfMonth: getWeekOfMonth(date)
        })
        setLimitDialog({
            modality,
            currentMax: limit?.max ?? '',
            templateId: limit?.templateId || null,
            count
        })
    }, [canEdit, date])

    const saveLimitDialog = useCallback(async () => {
        const { modality, currentMax, templateId } = limitDialog
        const val = currentMax === '' ? null : Number(currentMax)
        if (limitRecurring) {
            const daysOfWeek = limitRecForm.daysOfWeek.length ? limitRecForm.daysOfWeek : [utcDow(date)]
            const recurrence = {
                pattern: limitRecForm.pattern,
                interval: limitRecForm.pattern === 'weekly' ? limitRecForm.interval : 1,
                startDate: date,
                ...(limitRecForm.pattern === 'monthly' ? {
                    monthlyMode: limitRecForm.monthlyMode,
                    dayOfMonth: limitRecForm.monthlyMode === 'dayOfMonth' ? limitRecForm.dayOfMonth : undefined,
                    weekOfMonth: limitRecForm.monthlyMode === 'weekOfMonth' ? limitRecForm.weekOfMonth : undefined
                } : {})
            }
            if (templateId) {
                await UpdateModalityLimitTemplate(orgId, { templateId, max: val, daysOfWeek, recurrence })
            } else {
                await CreateModalityLimitTemplate(orgId, { modality, max: val, daysOfWeek, recurrence })
            }
        } else {
            await UpsertModalityLimitOverride(orgId, { date, modality, max: val, templateId: null })
        }
        setLimitDialog(null)
        fetchAll()
    }, [limitDialog, limitRecurring, limitRecForm, orgId, date, fetchAll])

    const saveColumnsDialog = useCallback(async () => {
        const allSelected = columnsEdit.length === 0 || columnsEdit.length === MODALITY_OPTIONS.length
        const newSettings = { ...access.org.settings }
        if (allSelected) {
            delete newSettings.modalityColumns
        } else {
            newSettings.modalityColumns = columnsEdit
        }
        await UpdateOrgSettings(orgId, newSettings).catch(() => {})
        setModalityColumns(allSelected ? MODALITY_OPTIONS : columnsEdit)
        setColumnsDialog(false)
    }, [columnsEdit, orgId, access.org.settings])

    // --- Render ---
    const studyName = (s) => s?.PatientMainDicomTags?.PatientName || s?.userData?.PatientMainDicomTags?.PatientName || 'Unknown'
    const studyDesc = (s) => s?.MainDicomTags?.StudyDescription || s?.userData?.MainDicomTags?.StudyDescription || ''
    const studyPid = (s) => s?.PatientMainDicomTags?.PatientID || s?.userData?.PatientMainDicomTags?.PatientID || ''

    return (
        <>
        <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Unscheduled panel */}
            {collapsed ? (
                <Paper variant="outlined" sx={{ width: 28, flexShrink: 0, maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', overflow: 'hidden' }} onClick={() => setCollapsed(false)}>
                    <IconButton size="small" sx={{ mt: 0.5 }}><ChevronRightIcon fontSize="small" /></IconButton>
                    <Typography variant="caption" sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'text.secondary', mt: 1, userSelect: 'none' }}>Unscheduled</Typography>
                </Paper>
            ) : (
                <Box sx={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" size="small" fullWidth onClick={() => navigate('/imaging-consult')}>+ Add New</Button>
                <Paper variant="outlined" sx={{ flex: 1, maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                    onDrop={e => {
                        e.preventDefault()
                        try {
                            const data = JSON.parse(e.dataTransfer.getData('text/plain'))
                            if (data.studyId) handleUnbook(data.studyId)
                        } catch (_) {}
                    }}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>Unscheduled ({unscheduled.length})</Typography>
                        <IconButton size="small" onClick={() => setCollapsed(true)}><ChevronLeftIcon fontSize="small" /></IconButton>
                    </Box>
                    <List dense disablePadding>
                        {unscheduled.map(study => (
                            <React.Fragment key={study.id}>
                                <ListItem
                                    draggable
                                    onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ studyId: study.id }))}
                                    onClick={() => {
                                        setStudyDetailDialog({ studyId: study.id, study, showPdf: false, pdfStudyId: null })
                                        GetStudy(study.id, { org: orgId }).then(s => setStudyDetailDialog(d => d?.studyId === study.id ? { ...d, study: s } : d)).catch(() => {})
                                    }}
                                    sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, '&:hover': { bgcolor: 'action.hover' }, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                        <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={studyName(study)}
                                        secondary={[studyPid(study), studyDesc(study)].filter(Boolean).join(' · ')}
                                        primaryTypographyProps={{ fontSize: 12, noWrap: true }}
                                        secondaryTypographyProps={{ fontSize: 10, noWrap: true }}
                                    />
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                        {!unscheduled.length && (
                            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
                                All studies scheduled
                            </Typography>
                        )}
                    </List>
                </Paper>
                </Box>
            )}

            {/* Grid */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    {canEdit && (
                        <IconButton size="small" onClick={() => { setColumnsEdit(modalityColumns); setColumnsDialog(true) }}>
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                <ModalityGrid
                    date={date}
                    slots={slots}
                    limits={limits}
                    countByModality={countByModality}
                    visibleModalities={modalityColumns}
                    onDateChange={setDate}
                    onDrop={handleDrop}
                    onSlotClick={setSlotDetailDialog}
                    onHeaderClick={handleHeaderClick}
                    editable={canEdit}
                />
            </Box>
        </Box>

        {/* Slot detail dialog */}
        {slotDetailDialog && (() => {
            const slot = slotDetailDialog
            const study = slot.studyId
            const studyId = String(study?.id || study?._id || slot.studyId)
            return (
                <Dialog open onClose={() => setSlotDetailDialog(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={slot.modality} size="small" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{studyName(study)}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{studyPid(study)}{studyDesc(study) ? ` · ${studyDesc(study)}` : ''}</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <TimePicker label="Start"
                                    value={dayjs(`2000-01-01T${slot.startTime}`)}
                                    onChange={v => v && setSlotDetailDialog(d => ({ ...d, startTime: v.format('HH:mm') }))} />
                                <TimePicker label="End"
                                    value={dayjs(`2000-01-01T${slot.endTime}`)}
                                    onChange={v => v && setSlotDetailDialog(d => ({ ...d, endTime: v.format('HH:mm') }))} />
                            </Stack>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={() => handleUnbook(studyId)}>Unschedule</Button>
                        <Button onClick={() => setSlotDetailDialog(null)}>Cancel</Button>
                        <Button onClick={() => {
                            handleUpdateSlotTime(slot.id, slot.startTime, slot.endTime)
                        }}>Save Time</Button>
                        <Button variant="contained" onClick={() => {
                            setSlotDetailDialog(null)
                            setStudyDetailDialog({ studyId, study, showPdf: false, pdfStudyId: null })
                            GetStudy(studyId, { org: orgId }).then(s => setStudyDetailDialog(d => d?.studyId === studyId ? { ...d, study: s } : d)).catch(() => {})
                        }}>View Details</Button>
                    </DialogActions>
                </Dialog>
            )
        })()}

        {/* Full study detail dialog */}
        <Dialog open={!!studyDetailDialog} onClose={() => setStudyDetailDialog(null)} maxWidth="md" fullWidth>
            <DialogActions sx={{ pb: 0 }}>
                {studyDetailDialog?.studyId && !studyDetailDialog?.study?.completeBy && (
                    <Button variant="contained" onClick={() => { setStudyDetailDialog(null); navigate(`/studies/${studyDetailDialog.studyId}/consult`) }}>Consult</Button>
                )}
                <Button onClick={() => setStudyDetailDialog(null)}>Close</Button>
            </DialogActions>
            <DialogContent sx={{ pt: 0 }}>
                {studyDetailDialog?.study && (
                    <>
                        <ReportingData study={studyDetailDialog.study} section="consult" hideFreeze hideConsult sx={{ px: 1.5, pt: 2 }} />
                        <DetailPanelContent
                            org={orgId}
                            row={studyDetailDialog.study}
                            setPdfDialogState={({ studyId }) => setStudyDetailDialog(d => ({ ...d, showPdf: true, pdfStudyId: studyId }))}
                            medDream={(uid) => GetToken({ data: uid }).then(r => r.accessToken && window.open(`https://dream.pacs.corridor.vet/?token=${r.accessToken}`, '_blank')).catch(() => {})}
                        />
                    </>
                )}
                {studyDetailDialog?.showPdf && (
                    <PdfPreview open studyId={studyDetailDialog.pdfStudyId} setDialogState={() => setStudyDetailDialog(d => ({ ...d, showPdf: false }))} />
                )}
            </DialogContent>
        </Dialog>

        {/* MWL prompt */}
        {mwlEnabled && mwlPromptDialog && (
            <Dialog open onClose={() => setMwlPromptDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Send to Modality Worklist?</DialogTitle>
                <DialogContent>
                    <ModalityWorklistFields
                        study={mwlPromptDialog.study || {}}
                        required
                        onMWLChange={setMwlData}
                        initialData={mwlInitialData}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMwlPromptDialog(null)}>Skip</Button>
                    <Button variant="contained" onClick={() => {
                        MwlEdit(mwlPromptDialog.studyId, { ...mwlData, aetitle }).catch(() => {})
                        setMwlPromptDialog(null)
                    }}>Send to MWL</Button>
                </DialogActions>
            </Dialog>
        )}

        {/* Modality mismatch warning */}
        <Dialog open={!!mismatchDialog} onClose={() => setMismatchDialog(null)} maxWidth="xs">
            <DialogTitle>Modality Mismatch</DialogTitle>
            <DialogContent>
                <Alert severity="warning">
                    This study has a Modality Worklist entry for <strong>{mismatchDialog?.existingModality}</strong>. Scheduling under <strong>{mismatchDialog?.newModality}</strong> will update the worklist modality. Proceed?
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setMismatchDialog(null)}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={handleMismatchConfirm}>Update & Schedule</Button>
            </DialogActions>
        </Dialog>

        {/* Duplicate prompt */}
        <Dialog open={!!duplicateDialog} onClose={() => setDuplicateDialog(null)} maxWidth="xs">
            <DialogTitle>Already Scheduled</DialogTitle>
            <DialogContent>
                <Alert severity="info">
                    This study is already scheduled under <strong>{duplicateDialog?.fromModality}</strong>. Duplicate it for <strong>{duplicateDialog?.toModality}</strong>?
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDuplicateDialog(null)}>Cancel</Button>
                <Button variant="contained" onClick={handleDuplicateConfirm}>Duplicate & Schedule</Button>
            </DialogActions>
        </Dialog>

        {/* Limit dialog */}
        {/* Limit dialog */}
        {canEdit && (
            <Dialog open={!!limitDialog} onClose={() => setLimitDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Max {limitDialog?.modality} per Day</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Currently scheduled: {limitDialog?.count ?? 0}
                        </Typography>
                        <TextField label="Max per day (blank = unlimited)" type="number" fullWidth
                            value={limitDialog?.currentMax ?? ''}
                            onChange={e => setLimitDialog(d => ({ ...d, currentMax: e.target.value }))} />
                        <FormControlLabel
                            control={<Switch checked={limitRecurring} onChange={e => setLimitRecurring(e.target.checked)} />}
                            label="Recurring" />
                        {limitRecurring && (
                            <RecurrenceEditor
                                form={limitRecForm}
                                setForm={setLimitRecForm}
                                startDate={date}
                                onStartDateChange={val => {
                                    const dow = utcDow(val)
                                    setLimitRecForm(f => ({ ...f, daysOfWeek: [dow], dayOfMonth: utcDayOfMonth(val), weekOfMonth: getWeekOfMonth(val) }))
                                }}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLimitDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={saveLimitDialog}>
                        {limitRecurring ? 'Save Series' : 'Save This Day Only'}
                    </Button>
                </DialogActions>
            </Dialog>
        )}

        {/* Modality columns dialog */}
        {canEdit && (
            <Dialog open={columnsDialog} onClose={() => setColumnsDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Visible Modalities</DialogTitle>
                <DialogContent>
                    <Stack sx={{ mt: 1 }}>
                        {MODALITY_OPTIONS.map(m => (
                            <FormControlLabel key={m}
                                control={
                                    <Checkbox
                                        checked={columnsEdit.includes(m)}
                                        onChange={e => setColumnsEdit(prev =>
                                            e.target.checked ? [...prev, m] : prev.filter(x => x !== m)
                                        )}
                                    />
                                }
                                label={m}
                            />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setColumnsEdit(columnsEdit.length === MODALITY_OPTIONS.length ? [] : MODALITY_OPTIONS)} 
                        size="small">
                        {columnsEdit.length === MODALITY_OPTIONS.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setColumnsDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveColumnsDialog}>Save</Button>
                </DialogActions>
            </Dialog>
        )}
        </>
    )
}

export default ModalityScheduleCalendar
